import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, Grid, Plus, FolderOpen, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * MobileNav - Tab Bar Atmosférico v3.0
 * 
 * Specs:
 * - Touch targets: 56dp mínimo
 * - FAB coral no centro (Forge/Create)
 * - Glass effect backdrop
 * - Safe area padding
 * 
 * @see mobile-design/touch-psychology.md
 */
interface NavItem {
    id: string;
    icon: React.ElementType;
    label: string;
    path: string;
    isFab?: boolean;
}

const navItems: NavItem[] = [
    { id: "home", icon: Home, label: "Início", path: "/dashboard" },
    { id: "projects", icon: Grid, label: "Projetos", path: "/dashboard/tasks" },
    { id: "create", icon: Plus, label: "Criar", path: "/dashboard/nova-task", isFab: true },
    { id: "library", icon: FolderOpen, label: "Vault", path: "/dashboard/vault" },
    { id: "profile", icon: User, label: "Perfil", path: "/dashboard/configuracoes" },
];

export function MobileNav() {
    const location = useLocation();
    const navigate = useNavigate();

    const isActive = (path: string) => location.pathname === path;

    return (
        <nav
            className={cn(
                "fixed bottom-0 inset-x-0 z-50",
                "glass border-t border-gold-mist",
                "pb-safe" // Safe area for notched devices
            )}
            aria-label="Navegação principal"
        >
            <div className="flex items-center justify-around px-2 py-2">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    // FAB (Create button) - Central action
                    if (item.isFab) {
                        return (
                            <button
                                key={item.id}
                                onClick={() => navigate(item.path)}
                                className={cn(
                                    // Touch target 56dp
                                    "touch-target flex items-center justify-center",
                                    // FAB styling
                                    "w-14 h-14 -mt-6",
                                    "rounded-full bg-coral-electric shadow-coral-glow",
                                    // Animation
                                    "transition-transform duration-snap ease-spring",
                                    "hover:scale-105 active:scale-95",
                                    // Focus state
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-electric focus-visible:ring-offset-2 focus-visible:ring-offset-void-absolute"
                                )}
                                aria-label={item.label}
                            >
                                <Icon className="h-6 w-6 text-void-absolute" strokeWidth={2.5} />
                            </button>
                        );
                    }

                    // Regular nav item
                    return (
                        <button
                            key={item.id}
                            onClick={() => navigate(item.path)}
                            className={cn(
                                // Touch target 56dp
                                "touch-target flex flex-col items-center justify-center gap-1",
                                "min-w-[56px] min-h-[56px] px-2",
                                // State styling
                                "transition-all duration-instant",
                                active ? "text-gold-primary" : "text-textSecondary hover:text-textPrimary"
                            )}
                            aria-current={active ? "page" : undefined}
                            aria-label={item.label}
                        >
                            <Icon
                                className={cn(
                                    "h-5 w-5 transition-transform duration-snap",
                                    active && "scale-110"
                                )}
                                strokeWidth={active ? 2.5 : 2}
                            />
                            <span
                                className={cn(
                                    "text-micro",
                                    active ? "text-gold-primary" : "text-textMuted"
                                )}
                            >
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
}

/**
 * MobileLayout - Wrapper com Tab Bar
 * Adiciona padding-bottom para não sobrepor conteúdo
 */
interface MobileLayoutProps {
    children: React.ReactNode;
    hideNav?: boolean;
}

export function MobileLayout({ children, hideNav = false }: MobileLayoutProps) {
    return (
        <div className="min-h-screen bg-void-absolute">
            <main className={cn(
                "flex-1",
                !hideNav && "pb-24" // Space for tab bar
            )}>
                {children}
            </main>
            {!hideNav && <MobileNav />}
        </div>
    );
}
